import React, { useState, useRef } from 'react';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography, Box, Alert, CircularProgress } from '@mui/material';
import { Upload as UploadIcon } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTDUConversion } from '../../common/hooks/useTDUConversion';

interface FileUploadButtonProps {
  onUpload?: (file: File) => void;
}

// Map old app names to new ones
const APP_NAME_MAPPING: { [key: string]: string } = {
  'App.p.OutsideCondensation': 'special_condenser',
  'App.p.PoolBoiling': 'special_flooded_evaporator',
  'App.HeatExchangerWKC': 'coaxial_condenser',
  'App.HeatExchangerWKE': 'coaxial_evaporator'
};

const FileUploadButton: React.FC<FileUploadButtonProps> = ({ onUpload }) => {
  const [open, setOpen] = useState(false);
  const [permissionErrorOpen, setPermissionErrorOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileContent, setFileContent] = useState<any>(null);
  const [isConverting, setIsConverting] = useState(false);
  const [conversionError, setConversionError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { appAccess } = useAuth();
  const { convertTDUFile } = useTDUConversion();

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    // Check file extension
    if (!file.name.endsWith('.tdx') && !file.name.endsWith('.tdu')) {
      console.error('Invalid file extension:', file.name);
      alert('Please select a valid .tdx or .tdu file');
      return;
    }

    setSelectedFile(file);
    setIsConverting(false);
    setConversionError(null);
    
    try {
      const content = await file.text();
      
      const parsedContent = JSON.parse(content);

      // Handle TDU files differently
      if (file.name.endsWith('.tdu')) {
        // Get app name from the TDU file
        const tduAppName = parsedContent.appName;
        if (!tduAppName) {
          setConversionError('Invalid TDU file: missing appName');
          return;
        }

        setIsConverting(true);
        const result = await convertTDUFile(file, tduAppName);
        
        if (result.error) {
          setConversionError(result.error);
          return;
        }
        
        // Convert the TDU data to match TDX format
        parsedContent.data = result.data;
        
        // Map the old app name to the new one for metadata
        const newAppType = APP_NAME_MAPPING[tduAppName];
        if (!newAppType) {
          console.error('Unknown app name:', tduAppName);
          setConversionError(`Unsupported application type: ${tduAppName}`);
          return;
        }

        parsedContent.metadata = {
          type: newAppType,
          version: '1.0',
          exportDate: new Date().toISOString()
        };
      }

      setFileContent(parsedContent);
      setOpen(true);
    } catch (error) {
      console.error('Error processing file:', error);
      setConversionError('Error reading file. Please make sure it\'s a valid file.');
    } finally {
      setIsConverting(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedFile(null);
    setFileContent(null);
    setConversionError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handlePermissionErrorClose = () => {
    setPermissionErrorOpen(false);
    handleClose();
  };

  const handleConfirm = () => {
    if (!fileContent?.metadata?.type) {
      alert('Invalid file format: missing metadata');
      return;
    }

    // Map the app and type to the correct route and required permission
    const routeMap: { [key: string]: { route: string; permission: string } } = {
      'special_flooded_evaporator': {
        route: '/special-flooded-evaporator',
        permission: 'thermals.ui_special_flooded_evaporator'
      },
      'special_condenser': {
        route: '/special-condenser',
        permission: 'thermals.ui_special_condenser'
      },
      'coaxial_evaporator': {
        route: '/coaxial-evaporator',
        permission: 'thermals.ui_coaxial_evaporator'
      },
      'coaxial_condenser': {
        route: '/coaxial-condenser',
        permission: 'thermals.ui_coaxial_condenser'
      },
      'cold_plate': {
        route: '/cold-plate',
        permission: 'microcool.ui_calculate'
      },
      'flooded_evaporator_rating': {
        route: '/flooded-evaporator',
        permission: 'flooded_evaporator.ui_calculate'
      },
      'flooded_evaporator_selection': {
        route: '/flooded-evaporator',
        permission: 'flooded_evaporator.ui_select'
      },
      'dry_expansion_evaporator_rating': {
        route: '/dry-expansion-evaporator',
        permission: 'dry_expansion_evaporator.ui_calculate'
      },
      'dry_expansion_evaporator_selection': {
        route: '/dry-expansion-evaporator',
        permission: 'dry_expansion_evaporator.ui_select'
      }
    };

    const routeInfo = routeMap[fileContent.metadata.type];
    if (!routeInfo) {
      alert('Unknown application type');
      return;
    }

    // Check if user has access to the corresponding app
    const hasAppAccess = appAccess?.accessible_apps.some(app => app.app_path === routeInfo.route) ?? false;
    if (!hasAppAccess) {
      setPermissionErrorOpen(true);
      return;
    }

    // If we're already in the target app, just update the state
    if (location.pathname === routeInfo.route) {
      navigate(routeInfo.route, { 
        state: { 
          importedData: {
            input_data: fileContent.data,
            system_of_units: fileContent.data.system_of_units || 'SI',
            metadata: fileContent.metadata
          },
          fromNavigation: false 
        } 
      });
    } else {
      // If we're in a different app or on the homepage, navigate to the target app
      navigate(routeInfo.route, { 
        state: { 
          importedData: {
            input_data: fileContent.data,
            system_of_units: fileContent.data.system_of_units || 'SI',
            metadata: fileContent.metadata
          },
          fromNavigation: true 
        } 
      });
    }
    handleClose();
  };

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept=".tdx,.tdu"
        style={{ display: 'none' }}
      />
      <Button
        variant="contained"
        startIcon={<UploadIcon />}
        onClick={handleClick}
        sx={{ mr: 1 }}
      >
        Import File
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Import File</DialogTitle>
        <DialogContent>
          {isConverting ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Converting TDU file...</Typography>
            </Box>
          ) : conversionError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {conversionError}
            </Alert>
          ) : selectedFile && fileContent && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                File Details:
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Name: {selectedFile.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Size: {(selectedFile.size / 1024).toFixed(2)} KB
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Type: {fileContent.metadata?.type || 'Unknown'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Version: {fileContent.metadata?.version || 'Unknown'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Export Date: {fileContent.metadata?.exportDate || 'Unknown'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button 
            onClick={handleConfirm} 
            variant="contained" 
            color="primary"
            disabled={isConverting || !!conversionError}
          >
            Import
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={permissionErrorOpen} 
        onClose={handlePermissionErrorClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Access Denied</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mt: 2 }}>
            You do not have permission to access this type of file. Please contact your administrator if you believe this is an error.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePermissionErrorClose} variant="contained" color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FileUploadButton; 